import React, { useState, useEffect } from "react";
import Modal from "../../UI/Modal";
import Button from "../../UI/Button";
import Select from "../../UI/Select";
import { ReportMetadataGroup } from "../../../models/pageModels/ReportLibrary";

interface GroupReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableGroups: ReportMetadataGroup[];
  selectedItems: { id: string; name: string }[];
  onConfirm: (mode: "new" | "existing", data: { groupId?: string; groupName?: string }) => Promise<void>;
}

const GroupReportsModal: React.FC<GroupReportsModalProps> = ({
  isOpen,
  onClose,
  availableGroups,
  selectedItems,
  onConfirm,
}) => {
  const [isNewGroup, setIsNewGroup] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsNewGroup(false);
      setSelectedGroupId("");
      setNewGroupName("");
      setIsSubmitting(false);
    }
  }, [isOpen, availableGroups.length]);

  const handleConfirm = async () => {
    if (!isNewGroup && !selectedGroupId) return;
    if (isNewGroup && !newGroupName.trim()) return;

    setIsSubmitting(true);
    await onConfirm(isNewGroup ? "new" : "existing", {
      groupId: selectedGroupId,
      groupName: newGroupName,
    });
    setIsSubmitting(false);
  };

  return (
    <Modal
      id="group-reports-modal"
      title="Group Reports"
      className="small padding-horizontal-5"
      visible={isOpen}
      onClose={onClose}
    >
      {isOpen && (
        <div className="row">
          <div className="column">
            <div className="form-group margin-bottom-4">
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isNewGroup}
                onChange={(e) => setIsNewGroup(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Create a new group
            </label>
          </div>

            {!isNewGroup ? (
              <Select
                id="group-reports-select"
                label="Select Group"
                required={true}
                value={selectedGroupId}
                onChange={(val: string) => setSelectedGroupId(val)}
              >
                <option value="">-- Select group --</option>
                {availableGroups.map((grp) => (
                  <option key={grp.id} value={grp.id}>
                    {grp.name}
                  </option>
                ))}
              </Select>
            ) : (
              <div className="form-group">
                <label className="label" htmlFor="new-group-name">
                  New Group Name
                </label>
                <input
                  type="text"
                  id="new-group-name"
                  className="input"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name..."
                />
              </div>
            )}

            {selectedItems.length > 0 && (
              <div className="margin-top-4">
                <p className="text-bold margin-bottom-2">Reports to group ({selectedItems.length}):</p>
                <ul style={{ paddingLeft: '20px', maxHeight: '150px', overflowY: 'auto' }}>
                  {selectedItems.map((item) => (
                    <li key={item.id} className="text-small margin-bottom-1">
                      {item.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="row justify-content-between margin-top-4">
              <div className="column-auto">
                <Button
                  id="cancel-select-modal"
                  className="secondary"
                  text="Cancel"
                  onClick={onClose}
                />
              </div>
              <div className="column-auto">
                <Button
                  id="confirm-select-modal"
                  text="Save"
                  className="button primary"
                  loading={isSubmitting}
                  disabled={
                    (!isNewGroup && !selectedGroupId) ||
                    (isNewGroup && !newGroupName.trim())
                  }
                  onClick={handleConfirm}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default GroupReportsModal;